import { useCallback, useEffect, useRef, useState } from "react";

// ----- Module-level singleton -----
// Ensures only ONE message can play at a time across every hook instance.
const subscribers = new Set();
let activeId = null;

const notify = () => {
    subscribers.forEach((fn) => fn(activeId));
};

const stopAny = () => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    try {
        window.speechSynthesis.cancel();
    } catch {
        /* noop */
    }
    activeId = null;
    notify();
};

// Preferred voice order: warmest natural-sounding female voices per platform,
// then graceful fallback to any English voice.
const VOICE_PREFERENCES = [
    "Samantha",
    "Ava",
    "Ava (Premium)",
    "Karen",
    "Moira",
    "Serena",
    "Google UK English Female",
    "Google US English",
    "Microsoft Aria Online (Natural) - English (United States)",
    "Microsoft Jenny Online (Natural) - English (United States)",
    "Microsoft Sonia Online (Natural) - English (United Kingdom)",
];

const pickVoice = () => {
    if (typeof window === "undefined" || !window.speechSynthesis) return null;
    const voices = window.speechSynthesis.getVoices() || [];
    if (voices.length === 0) return null;

    for (const name of VOICE_PREFERENCES) {
        const hit = voices.find((v) => v.name === name);
        if (hit) return hit;
    }
    const en = voices.filter((v) => /^en/i.test(v.lang || ""));
    const female = en.find((v) =>
        /female|samantha|karen|victoria|moira|zira|aria|jenny|sonia/i.test(
            v.name || "",
        ),
    );
    return female || en[0] || voices[0] || null;
};

/**
 * Browser Text-to-Speech hook. Supports at-most-one playback globally —
 * tapping a new message auto-stops the previous utterance. Playback
 * starts in < 100ms on all supported browsers (no network).
 */
export function useSpeechPlayback() {
    const [speakingId, setSpeakingId] = useState(null);
    const mountedRef = useRef(true);

    const isSupported =
        typeof window !== "undefined" && !!window.speechSynthesis;

    useEffect(() => {
        mountedRef.current = true;
        const handler = (id) => {
            if (mountedRef.current) setSpeakingId(id);
        };
        subscribers.add(handler);
        return () => {
            mountedRef.current = false;
            subscribers.delete(handler);
        };
    }, []);

    // Chrome loads voices async — force a warm-up so pickVoice() has data
    // before the first play request.
    useEffect(() => {
        if (!isSupported) return;
        const synth = window.speechSynthesis;
        const warm = () => {
            try {
                synth.getVoices();
            } catch {
                /* noop */
            }
        };
        warm();
        const prev = synth.onvoiceschanged;
        synth.onvoiceschanged = warm;
        return () => {
            synth.onvoiceschanged = prev || null;
        };
    }, [isSupported]);

    const toggle = useCallback(
        (id, text) => {
            if (!isSupported || !text || !id) return;
            const synth = window.speechSynthesis;

            // Tap again on the currently playing message → stop.
            if (activeId === id) {
                stopAny();
                return;
            }

            // Stop whatever else was playing, then queue this one.
            stopAny();

            const utt = new window.SpeechSynthesisUtterance(text);
            const voice = pickVoice();
            if (voice) utt.voice = voice;
            utt.rate = 1.0;
            utt.pitch = 1.02; // very slight warmth lift
            utt.volume = 1.0;
            utt.lang = voice?.lang || "en-US";

            utt.onstart = () => {
                activeId = id;
                notify();
            };
            utt.onend = () => {
                if (activeId === id) {
                    activeId = null;
                    notify();
                }
            };
            utt.onerror = () => {
                if (activeId === id) {
                    activeId = null;
                    notify();
                }
            };

            try {
                synth.speak(utt);
            } catch {
                activeId = null;
                notify();
            }
        },
        [isSupported],
    );

    const stop = useCallback(() => stopAny(), []);

    return { isSupported, speakingId, toggle, stop };
}
